import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { preFetchCompany as preFetchCompanyRoutine } from '@/pages/CompanyReport/redux/routines';
import { push, replace } from 'redux-first-router';
import { getPageFromString } from '@/utilities/getPageFromString';
import {
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_CREATE_CRITERIA,
} from '@/constants/routes';
import queryString from 'query-string';
import { useTracking } from 'react-tracking';
import { getPath } from '@/router/utils';
import { checkSupportedMarket } from '@/utilities/markets';
import { checkSupportedIndustry } from '@/utilities/industries';
import { SCREENER_RULES_PERSISTED_KEY } from '@/pages/Screener/constants';
import { usePersistentStorage } from '@/hooks/usePersistentStorage';

type DiscoverRoutes =
  | typeof ROUTE_INVESTING_IDEA_DISCOVERY_PAGE
  | typeof ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
  | typeof ROUTE_INVESTING_IDEA_MARKET;

interface PathRequest {
  type: DiscoverRoutes;
  payload: {
    id: string;
    name: string;
    market: PrimaryCountryISOElseGlobal;
    industry?: string | 'any';
    query?: {
      page: string;
    };
  };
}

interface Location {
  type: string;
  id: string;
  name: string;
  market?: PrimaryCountryISOElseGlobal;
  industry?: string | 'any';
  pageQuery: number;
}

interface NavigateTo {
  route: DiscoverRoutes;
  market: PrimaryCountryISOElseGlobal;
  industry?: string | 'any';
  excludePageQuery?: boolean;
  method?: 'replace' | 'push';
}

type Route = {
  type: string;
  payload: Record<string, any>;
};

interface ReduxState extends Omit<Location, 'type'> {
  canonicalUrl: string;
  navigateTo: (config: NavigateTo) => void;
  preFetchCompany: (id: string) => void;
  getPaginatedRoute: (route: DiscoverRoutes, page: number) => Route;
  onAdvancedFilters: (updatedRules: string | undefined) => void;
}

export const getDiscoverPagePath = ({
  route,
  id,
  name,
  market,
  industry,
  excludePageQuery = true,
}: {
  route: DiscoverRoutes;
  id: string;
  name: string;
  market: PrimaryCountryISOElseGlobal;
  excludePageQuery?: boolean;
  industry?: string;
}) => {
  const pathRequest: PathRequest = {
    payload: {
      id,
      name,
      market,
      query: excludePageQuery ? undefined : { page: '1' },
    },
    type: route,
  };
  if (typeof industry !== 'undefined' && industry !== 'any') {
    pathRequest.payload.industry = industry;
  }
  try {
    return getPath(pathRequest);
  } catch (e) {
    console.warn(e);
  }
};

const getQuery = (location: LocationState) =>
  RUNTIME_ENV === 'server'
    ? location.query
    : queryString.parse(window.location.search);

export function useReduxStateForDiscover(): ReduxState {
  const { trackEvent } = useTracking();
  const { market, industry, name, id, pageQuery, type } = useSelector<
    {
      location: LocationState;
    },
    Location
  >((state) => {
    const { id, name, market, industry } = state.location.payload;
    /**
     * redux-first-router stores a query param over different paths.
     * So imagine if from /path/us/banks?page=3 the user then navigates to /path/uk/banks.
     * { page: 3 } will persist in Redux state. But what if /path/uk/banks only has 1 page.
     * The following logic looks to only use redux state server side where window is undefined
     */
    const query = getQuery(state.location);
    const safePage = getPageFromString(query?.page as string | undefined);
    return {
      type: state.location.type,
      id,
      name,
      market: checkSupportedMarket(market),
      industry: checkSupportedIndustry(industry),
      pageQuery: safePage < 1 ? 1 : safePage,
    };
  }, shallowEqual);

  const canonicalUrl = useMemo(
    () =>
      getDiscoverPagePath({
        route: type as DiscoverRoutes,
        id,
        name,
        market: market || 'global',
        industry,
      }),
    [id, name, market, industry, type]
  );

  const dispatch = useDispatch();

  const preFetchCompany = useCallback(
    (companyId: string) => {
      dispatch(preFetchCompanyRoutine.trigger({ id: companyId }));
    },
    [dispatch]
  );

  const { set } = usePersistentStorage();

  const onAdvancedFilters = useCallback(
    (updatedRules: string | undefined) => {
      trackEvent({
        type: 'track',
        subject: 'advanced-filter',
        action: 'click',
      });
      if (updatedRules) {
        set(SCREENER_RULES_PERSISTED_KEY, updatedRules);
      }
      const path = getPath({
        type: ROUTE_CREATE_CRITERIA,
        payload: {},
      });
      push(path);
    },
    [set, trackEvent]
  );

  const navigateTo = useCallback(
    ({
      method = 'push',
      route,
      market,
      industry,
      excludePageQuery,
    }: NavigateTo) => {
      const path = getDiscoverPagePath({
        route,
        id,
        name,
        market,
        industry,
        excludePageQuery,
      });
      if (typeof path === 'undefined') return;
      try {
        if (method === 'replace') {
          replace(path);
          return;
        }
        push(path);
      } catch (e) {
        console.warn(e);
      }
    },
    [id, name]
  );

  const getPaginatedRoute = useCallback(
    (route: DiscoverRoutes, page: number) => {
      return {
        type: route,
        payload: {
          market: market,
          industry: industry,
          id,
          name,
          query: {
            page: String(page),
          },
        },
      };
    },
    [id, name, market, industry]
  );

  return {
    id,
    name,
    market,
    industry,
    pageQuery,
    canonicalUrl: canonicalUrl || '',
    preFetchCompany,
    onAdvancedFilters,
    navigateTo,
    getPaginatedRoute,
  };
}
